import {
  AlertCircle,
  Bot,
  CheckCircle,
  ChevronRight,
  Cog,
  FolderOpen,
  Loader2,
  Plug,
  Sparkles,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { SandboxRunCodeDisplay } from './SandboxRunCodeDisplay';
import type { ToolCategory } from './task-detail-types';
import { getCategoryBadgeColor, getCategoryLabel } from './task-detail-utils';

/**
 * カテゴリアイコンを取得
 */
function getCategoryIcon(category?: ToolCategory) {
  switch (category) {
    case 'builtin':
      return <Cog className="w-3 h-3" />;
    case 'sandbox':
      return <FolderOpen className="w-3 h-3" />;
    case 'skill':
      return <Sparkles className="w-3 h-3" />;
    case 'subagent':
      return <Bot className="w-3 h-3" />;
    case 'mcp':
      return <Plug className="w-3 h-3" />;
    default:
      return <Wrench className="w-3 h-3" />;
  }
}

/**
 * ステータスアイコンを取得
 */
function getStatusIcon(status?: 'running' | 'completed' | 'failed' | 'cancelled') {
  switch (status) {
    case 'running':
      return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'cancelled':
      return <AlertCircle className="w-4 h-4 text-amber-500" />;
    default:
      return <Wrench className="w-4 h-4 text-muted-foreground" />;
  }
}

/**
 * ツール呼び出しカードコンポーネント（desktop-appと同じ構造）
 */
export function ToolCallCard({
  toolName,
  category,
  status,
  intent,
  args,
  result,
  isError,
  isCancelled,
}: {
  toolName: string;
  category: ToolCategory | undefined;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  intent: string | undefined;
  args: unknown;
  result: unknown;
  isError: boolean | undefined;
  isCancelled: boolean | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [showOutput, setShowOutput] = useState(false);

  const isRunning = status === 'running';
  const isFailed = status === 'failed';

  // intent がある場合は intent を表示、ない場合は「ツールを実行中」を表示
  const displayText = intent || 'ツールを実行中';
  const maxLength = 50;
  const truncatedText =
    displayText.length > maxLength ? `${displayText.slice(0, maxLength)}...` : displayText;

  return (
    <div className="my-2 rounded-md border border-border bg-card">
      {/* ヘッダー */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 p-2 text-left text-sm hover:bg-muted/50 transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <span
          className={`group/badge inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium transition-all duration-200 ${getCategoryBadgeColor(category)}`}
        >
          {getCategoryIcon(category)}
          <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover/badge:max-w-24 group-hover/badge:opacity-100">
            {getCategoryLabel(category)}
          </span>
        </span>
        <span className={`font-medium text-foreground ${isRunning ? 'animate-pulse' : ''}`}>
          {truncatedText}
        </span>
        {isRunning && <span className="text-xs text-muted-foreground">...</span>}
        {isFailed && <span className="text-xs text-red-400">失敗</span>}
        {isCancelled && <span className="text-xs text-amber-500">キャンセル</span>}
        <span className="ml-auto">{getStatusIcon(status)}</span>
      </button>

      {/* 展開時のコンテンツ */}
      {isOpen &&
        (toolName === 'sandbox__runCode' ? (
          /* sandbox__runCode 専用表示 */
          <div className="border-t border-border p-2">
            <SandboxRunCodeDisplay args={args} result={result} />
          </div>
        ) : (
          <>
            {/* ツール入力 */}
            <div className="border-t border-border">
              <button
                type="button"
                onClick={() => setShowInput(!showInput)}
                className="flex w-full items-center gap-1 p-2 text-left hover:bg-muted/30 transition-colors"
              >
                <ChevronRight
                  className={`w-3 h-3 text-muted-foreground transition-transform ${showInput ? 'rotate-90' : ''}`}
                />
                <span className="text-xs text-muted-foreground">ツール: </span>
                <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono font-semibold text-foreground">
                  {toolName}
                </code>
              </button>
              {showInput && args !== undefined && (
                <div className="px-2 pb-2">
                  <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted/50 p-2 text-xs text-foreground">
                    {typeof args === 'string' ? args : JSON.stringify(args, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* ツール出力 */}
            {result !== undefined && (
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowOutput(!showOutput)}
                  className="flex w-full items-center gap-1 p-2 text-left hover:bg-muted/30 transition-colors"
                >
                  <ChevronRight
                    className={`w-3 h-3 text-muted-foreground transition-transform ${showOutput ? 'rotate-90' : ''}`}
                  />
                  <span className="text-xs text-muted-foreground">
                    {isError ? 'エラー詳細' : isCancelled ? 'キャンセル' : '実行結果'}
                  </span>
                </button>
                {showOutput && (
                  <div className="px-2 pb-2">
                    <pre
                      className={`overflow-x-auto whitespace-pre-wrap break-all rounded p-2 text-xs ${
                        isError
                          ? 'bg-red-950/50 text-red-200'
                          : isCancelled
                            ? 'bg-amber-950/50 text-amber-200'
                            : 'bg-muted/50 text-foreground'
                      }`}
                    >
                      {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </>
        ))}
    </div>
  );
}
